import React from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import clsx from "clsx";

// Material
import { makeStyles } from "@material-ui/core/styles";
import ButtonBase from "@material-ui/core/ButtonBase";

// Assets
import kulpaIchip from "../../assets/icons/kulpaIchip.svg";
import colors from "../../config/colors";
import styles from "../../config/styles";
import pendingStatementIcon from "../../assets/icons/pendingStatementIcon.svg";

// Hooks
import useTranslate from "../../hooks/useTranslate";

// Actions
import { resetToggledCase } from "../../state/cases/actions";

// Components
import Link from "../Link";
import SimpleMenu from "../SimpleMenu";
import Text from "../Text";

const useStyles = makeStyles(theme => ({
  header: {
    padding: theme.spacing(3, 2),
    borderBottom: styles.values.border,
    display: "block"
  },
  contentColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  heading: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
    flex: 1
  },
  headingNewIntel: {
    backgroundColor: "#E8E7FC"
  },
  dateWrapper: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
    flex: 1
  },
  imgKulpaI: {
    marginLeft: "auto"
  },
  titleWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  titleWrapperReverse: {
    flexDirection: "row-reverse"
  },
  titleContent: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  new: {
    color: colors.rose,
    paddingTop: theme.spacing(1.5)
  },
  title: {
    color: ({ isIntel, actionRequired }) =>
      (actionRequired && colors.lockedCardText) ||
      (isIntel && colors.deepKoamaru)
  },
  titleCompact: {
    fontSize: 28
  },
  victimName: {
    fontSize: 14,
    fontWeight: theme.typography.fontWeightRegular
  },
  requiredText: {
    textTransform: "uppercase",
    color: colors.lockedCardText
  },
  requiredDate: {
    color: colors.lockedCardText
  }
}));

const CardHeader = ({
  actionRequired,
  compactTitle,
  date,
  isButton,
  isRestricted,
  options,
  showOptions,
  title,
  to,
  victimName,
  isIntel,
  intelIconLeft,
  showIntelIcon
}) => {
  const classes = useStyles({ isIntel, actionRequired });
  const dispatch = useDispatch();
  const getText = useTranslate();

  const reset = () => dispatch(resetToggledCase());

  const canShowMenu = !isRestricted && showOptions && options.length > 0;
  const showMenuInHeading = canShowMenu && actionRequired;
  const showMenuInTitle = canShowMenu && !actionRequired;

  const hasHeadingContent =
    Boolean(date) ||
    actionRequired ||
    (isIntel && intelIconLeft && showIntelIcon) ||
    (isIntel && !intelIconLeft);

  const menu = (
    <SimpleMenu title="casesPage.caseCard.menu.title" options={options} />
  );

  const content = (
    <div className={classes.contentColumn}>
      {!isRestricted && hasHeadingContent && (
        <div className={classes.heading}>
          <div className={classes.dateWrapper}>
            {isIntel && intelIconLeft && showIntelIcon && (
              <img src={kulpaIchip} alt="kulpa-i" />
            )}
            <Text
              type="contentLabel"
              className={clsx({ [classes.requiredDate]: actionRequired })}
            >
              {actionRequired && (
                <span className={classes.requiredText}>
                  {getText("common.prompts.actionRequired")}
                  {date && " - "}
                </span>
              )}
              {date}
            </Text>
            {isIntel && !intelIconLeft && (
              <img
                src={kulpaIchip}
                alt="kulpa-i"
                className={classes.imgKulpaI}
              />
            )}
          </div>
          {showMenuInHeading && menu}
        </div>
      )}
      <div
        className={clsx(classes.titleWrapper, {
          [classes.titleWrapperReverse]: showMenuInTitle
        })}
      >
        {showMenuInTitle && menu}
        <div className={classes.titleContent}>
          {actionRequired && <img src={pendingStatementIcon} alt="" />}
          <Text
            type="heading1"
            className={clsx(classes.title, {
              [classes.titleCompact]: compactTitle
            })}
          >
            {title}
          </Text>
        </div>
      </div>
      {victimName && (
        <Text type="contentLabel" className={classes.victimName}>
          {getText("common.userTypes.Victim")}: {victimName}
        </Text>
      )}
    </div>
  );

  return isButton ? (
    <ButtonBase
      className={clsx(classes.header, {
        [classes.headingNewIntel]: isIntel
      })}
      component={Link}
      onClick={reset}
      to={to}
    >
      {content}
    </ButtonBase>
  ) : (
    content
  );
};

CardHeader.propTypes = {
  compactTitle: PropTypes.bool,
  date: PropTypes.string,
  isButton: PropTypes.bool,
  isRestricted: PropTypes.bool,
  actionRequired: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  showOptions: PropTypes.bool,
  title: PropTypes.node.isRequired,
  to: PropTypes.string.isRequired,
  victimName: PropTypes.string,
  isIntel: PropTypes.bool.isRequired,
  intelIconLeft: PropTypes.bool,
  showIntelIcon: PropTypes.bool
};

CardHeader.defaultProps = {
  compactTitle: false,
  date: null,
  isButton: false,
  isRestricted: false,
  options: [],
  showOptions: false,
  victimName: null,
  actionRequired: false,
  intelIconLeft: false,
  showIntelIcon: true
};

export default CardHeader;
